/**
 * 거래 화면 (DESIGN 14.43, 14.151).
 *
 * ── 이 화면이 지켜야 하는 것 ────────────────────────────────────
 * 서버가 막아둔 사기 방지 장치가 **화면에서 보이지 않으면 막은 것이 아니다.**
 *
 * 목록이 바뀌면 양쪽 확정이 풀리는데, 그게 조용히 일어나면 사람은 **여전히
 * 확정한 줄 안다.** 그래서 확정 버튼이 풀릴 때 화면이 그 사실을 말한다.
 *
 * ── 왜 상대 칸을 크게 두나 ──────────────────────────────────────
 * 거래에서 잘못되는 순간은 **내가 뭘 주는지 헷갈릴 때가 아니라 상대가 뭘 주는지
 * 잘못 볼 때**다. 상대 칸을 위에 두고 먼저 읽히게 한다.
 */
import { S, esc, inTown } from '../../core/state.js';
import { on, send } from '../../core/net.js';
import { toast } from '../toast.js';
import { SFX } from '../../core/sound.js';
import { POTIONS, PASSIVE_BY_KEY, SKILL_IMPRINT_BY_KEY } from '../../world/map.js';
import { eqName, fold, gearEdge, gearTone, isResident, lore, muted, num, rollDesc, sec, split , junkName } from './common.js';
import { levelLabel } from '../../core/level-label.js';
import { starMark } from '../star.js';

/**
 * 거래 사거리(타일). **서버 `src/world/trade.ts` 의 `TRADE_RANGE` 와 같은 값이어야 한다.**
 *
 * 화면 문구에 6 이 두 군데 손으로 박혀 있었다. 서버가 값을 바꾸면 화면이 거짓말을
 * 하게 되므로 여기 한 곳으로 모았다. 클라가 이 값으로 하는 일은 **목록을 거르는 것**
 * 뿐이고, 실제 판정은 서버가 다시 한다 — 화면이 거른 것을 서버가 믿으면 그게 클라 권한이다.
 * R227 — 이름표 카드(`person.js`)도 이 값을 가져다 쓴다(세 번째 사본을 만들지 않는다).
 */
export const TRADE_RANGE = 6;

/** 서버가 보내는 것을 그대로 받는다. 클라가 따로 셈하지 않는다 — 두 진실이 생긴다 */
on('trade', (m) => {
  S.trade = m.with ? { with: m.with, mine: m.mine, theirs: m.theirs, theirGear: m.theirGear || {}, fail: m.fail || '' } : null;
  if (!S.trade) S.tradeAsk = null;
  window.dispatchEvent(new CustomEvent('unige:trade'));
});
/**
 * 누가 거래를 요청했다.
 *
 * **알림이 탭 배지 하나뿐이었다.** 14.43 이 "요청이 왔는데 알림이 없으면 그 요청은
 * 없는 것과 같다" 며 배지를 넣었는데, 배지는 화면 구석이라 탭 바를 안 보고 있으면
 * 모른다. 사용자 지적: *"반대 캐릭터에선 아무런 메세지가 안뜨니까 알지도 못하고"*.
 * 승낙할 방법이 없으면 **거래 기능 자체가 성립하지 않는다.**
 *
 * 그래서 눈(토스트)과 귀(소리)를 더한다. **로그 줄은 여기서 넣지 않는다** —
 * 서버가 보낸다(경계: 로그는 서버, 눈에 띄는 것은 클라). 양쪽이 각자 넣으면
 * 같은 말이 두 번 뜬다.
 *
 * 소리는 `shard` 를 쓴다. 새로 만들지 않고 있는 것 중에 골랐다 — 올라가는 세 음이라
 * 전투음·피격음과 안 섞이고, "누가 나를 부른다" 로 읽힌다. `warn` 은 위험 신호라
 * 거래 요청에 쓰면 사람이 도망칠 소리로 듣는다.
 */
on('tradeAsk', (m) => {
  S.tradeAsk = m.from;
  toast(`${m.from.name} 님이 거래를 요청했다. 거래 창에서 수락할 수 있다.`, 'trade', {
    label: '요청 보기', action: () => window.dispatchEvent(new CustomEvent('unige:panel', { detail: 'trade' })),
  });
  SFX.shard();
  window.dispatchEvent(new CustomEvent('unige:trade'));
});

/**
 * 최근 거래 기록. 서버가 **접속할 때 한 번, 거래가 끝나면 한 번 더** 보낸다.
 *
 * 화면이 이걸 안 그리면 서버가 남긴 기록은 **없는 것과 같다** — 이 레포 대표
 * 결함("보내는데 안 읽는 것")이고, 하필 재화가 오간 자리에 걸린다.
 * 무엇을 주고 무엇을 받았는지는 서버가 이미 사람이 읽을 한 줄로 만들어 보낸다.
 */
on('tradeLog', (m) => {
  S.tradeLog = Array.isArray(m.rows) ? m.rows : [];
  window.dispatchEvent(new CustomEvent('unige:trade'));
});
// DESIGN 14.599 6단계 — a trade the game server's town settled: its save appended this account's log — read it back (the source pushed it).
//   The region settles on its own journal commit and the save reaches D1 after it (the save outbox), so the page reads until the new line
//   shows: a second after, then every 2.5 seconds, three reads at most (독립 검토 지적 — one read could come before the save)
let tradeLogWatch = 0;
on('tradeSettled', () => {
  const last = () => (S.tradeLog || []).at(-1)?.at ?? 0;
  const before = last();
  const read = (tries) => {
    send({ t: 'tradeLogGet' });
    if (tries > 1) tradeLogWatch = setTimeout(() => { if (last() <= before) read(tries - 1); }, 2500);
  };
  clearTimeout(tradeLogWatch);
  tradeLogWatch = setTimeout(() => read(3), 1000);
});

/** 제안 한 줄 — 장비·재료·소모품·은화를 같은 모양으로 읽히게 */
function lines(o, eqOf) {
  const rows = [];
  for (const id of o.gear || []) {
    const e = eqOf(id);
    // `eqName` 이 이미 `+N` 을 붙인다 — 오른쪽에 또 적으면 `하급 손도끼 +3 +3` 이 된다.
    // 값이 두 번 보이면 사람은 **다른 값 두 개**로 읽는다.
    rows.push(e
      ? `<div class="kv gd" ${gearEdge(e)}><span style="color:${gearTone(e).tint}">`
        + `${eqName(e)}<br><small class="qt">${rollDesc(e)}</small></span></div>`
      // 라벨을 못 받은 장비만 여기로 온다(올린 직후 한 틱 등).
      // **모르면 모른다고 적는다.** 그럴듯한 이름을 지어내면 그게 거짓말이 된다.
      : `<div class="kv"><span>장비</span><b>1</b></div>`);
  }
  for (const [k, n] of Object.entries(o.junk || {})) {
    const book = PASSIVE_BY_KEY.get(k);
    const imprint = SKILL_IMPRINT_BY_KEY.get(k);
    rows.push(`<div class="kv"><span>${book ? `<b style="color:var(--gold)">${book.name}</b>`
      : imprint ? `<b style="color:${imprint.color}">${imprint.name}</b>` : junkName(k)}</span><b>${n}개</b></div>`);
  }
  for (const [k, n] of Object.entries(o.consum || {})) {
    const s = POTIONS.find((q) => q.id === k);
    rows.push(`<div class="kv"><span>${s ? s.name : k}</span><b>${n}개</b></div>`);
  }
  if (o.silver > 0) rows.push(`<div class="kv"><span>은화</span><b>${num(o.silver)}</b></div>`);
  return rows.length ? rows.join('') : muted('아직 아무것도 올리지 않았다.');
}

/** 내가 올릴 수 있는 것 — 가방에서 만든다. 손으로 나열하면 품목이 늘 때 낡는다 */
function myBag({ y, eq }, mine) {
  const equipped = y.inv.equipped || {};
  let h = '';
  // 낀 것은 칸 이름이 아니라 id 로 가린다 — 반지는 둘째 칸에 있을 수 있다(UNI-Q36)
  const worn = new Set(Object.values(equipped));
  const bag = eq.filter((e) => !worn.has(e.id) && e.guildBound == null);
  if (bag.length) {
    h += '<div class="row wrap">' + bag.map((e) => {
      const on0 = (mine.gear || []).includes(e.id);
      return `<button class="gd${on0 ? ' sel' : ''}" ${gearEdge(e)} onclick="act.tradePut('gear','${e.id}',0)">`
        // 여기도 같은 중복이었다 — `eqName` 이 `+N` 을 포함한다
        + `${on0 ? '↑ ' : ''}${eqName(e)}</button>`;
    }).join('') + '</div>';
  }
  const j = y.inv.junk || {};
  const c = y.inv.consum || {};
  const stack = (kind, box, nameOf) => Object.keys(box).filter((k) => box[k] > 0).map((k) => {
    const put = (kind === 'junk' ? mine.junk : mine.consum)[k] || 0;
    const have = box[k];
    const shown = Math.min(put, have);
    return `<div class="kv trade-stack">
      <div class="trade-stack-head"><span>${nameOf(k)}</span><output>${shown}/${have}</output></div>
      <div class="trade-stack-controls">
        <input type="range" min="0" max="${have}" step="1" value="${shown}"
          aria-label="${nameOf(k)} 거래 수량" data-max="${have}"
          oninput="act.tradePreview(this)" onchange="act.tradeRange(this,'${kind}','${k}')">
        <button aria-label="1개 줄이기" ${shown <= 0 ? 'disabled' : ''}
          onclick="act.tradePut('${kind}','${k}',${shown - 1})">−1</button>
        <button aria-label="1개 늘리기" ${shown >= have ? 'disabled' : ''}
          onclick="act.tradePut('${kind}','${k}',${shown + 1})">+1</button>
        <button ${shown >= have ? 'disabled' : ''} onclick="act.tradePut('${kind}','${k}',${have})">전부</button>
      </div></div>`;
  }).join('');
  h += stack('junk', j, junkName);
  h += stack('consum', c, (k) => (POTIONS.find((q) => q.id === k)?.name) || k);
  h += `<div class="kv trade-silver">
    <div class="trade-stack-head"><span>은화</span><output>${num(mine.silver)} / ${num(y.silver)}</output></div>
    <div class="trade-money-controls">
      <input id="tsilver" type="number" inputmode="numeric" min="0" max="${y.silver}" step="1"
        value="${mine.silver}" aria-label="거래할 은화" onkeydown="if(event.key==='Enter')act.tradeSilver()">
      <button onclick="act.tradeSilver()">적용</button>
      <button ${mine.silver >= y.silver ? 'disabled' : ''} onclick="act.tradePut('silver','',${y.silver})">전부</button>
      <button ${mine.silver <= 0 ? 'disabled' : ''} onclick="act.tradePut('silver','',0)">0으로</button>
    </div></div>`
    // R231 — 은화 칸이 왜 가방 은화까지만 되는지(상점·강화·탑은 창고 부족분을 쓴다)
    + muted('가방 은화만 올린다 · 창고 은화는 먼저 꺼내 온다');
  return h;
}

/**
 * 거래 창 — 받은 요청·가까운 사람(닫힌 동안) · 주고받는 목록과 확정 띠(열린 동안).
 *
 * Q276 — 전에는 맨 위(열린 거래는 목록 끝)에 파티 칸이 붙어 있었다(UNI-Q129). 파티는 이제 메뉴 › 사람 › 파티 한 곳이다.
 * ⚠ UNI-Q150 — **거래가 열리면 화면은 split(목록 + 하단 확정 띠)이고, split 은 #pbody 의 첫 자식이어야 한다.**
 * index.js 는 첫 자식이 split 일 때만 #pbody 를 세로 flex 로 바꿔 목록에 남는 높이를 준다(hassplit).
 */
export function render(ctx) {
  // Q276 — 파티 칸은 메뉴 › 사람 › 파티 한 곳으로 옮겼다(전에는 거래 창 맨 위·열린 거래의 목록 끝에도 있었다)
  return S.trade ? renderOpen(ctx, S.trade, S.trade.mine, S.trade.theirs) : renderIdle(ctx);
}

/** 거래가 안 열린 화면 — 받은 요청·가까운 사람·최근 기록 */
function renderIdle(ctx) {
  /**
   * 들어온 요청은 **거래 화면 안에서** 받는다.
   *
   * 화면 위에 덮개를 띄우지 않는 이유: 사냥 중에 덮개가 뜨면 그 자체가 방해이고,
   * 급하게 누르다 원치 않는 거래에 들어간다. 로그에 한 줄이 뜨고(서버가 보낸다),
   * 이 탭에 오면 받을 수 있다. **거절도 명시적으로 누를 수 있어야** 한다 —
   * 안 그러면 요청이 언제까지 유효한지 아무도 모른다.
   */
  if (S.tradeAsk) {
    return sec('받은 거래 요청',
      `<section class="ux-hero"><p class="ux-kicker">물건을 주고받기 전에</p><h3 class="ux-title">${esc(S.tradeAsk.name)} 님의 요청</h3><p class="ux-summary">Lv.${levelLabel(S.tradeAsk.level)} · 수락하면 교환할 물건을 함께 고른다.</p></section>`
      + muted(`서로 마을 안에서 ${TRADE_RANGE}타일 안에 있어야 한다. 요청이 만료됐다면 상대가 다시 신청해야 한다.`)
      + `<div class="row">
          <button class="wide ux-primary" onclick="act.tradeAccept('${esc(S.tradeAsk.id)}')">거래 시작하기</button>
          <button onclick="act.tradeDeny()">거절</button>
        </div>` + muted('시작만으로 물건이나 은화가 이동하지 않는다. 두 사람 모두 조건을 확정해야 교환된다.') + tradeLog());
  }
  /**
   * **가까운 사람 목록 — 거래를 여는 두 번째 길.**
   *
   * 전에는 여는 방법이 `Ctrl+클릭` **하나뿐**이었고, 이 화면은 그 방법을 글로
   * 설명만 했다. 그 하나가 **정확한 좌표를 요구한다** — 상대 위에 정확히 눌러야
   * 하고, 모바일에서는 손가락으로 사람 하나를 집어야 한다. 실제로 이 자리에서
   * 세 번 막혔다: 사용자가 조작을 찾아냈고, 알림이 없어 상대가 몰랐고,
   * 두 세션이 실물 확인을 하려다 **좌표를 못 찾아** 둘 다 실패했다.
   * 뿌리가 같다 — **여는 경로가 하나뿐이고 그게 조준을 요구한다.**
   *
   * 서버가 이미 AOI 안의 사람을 보내주므로(`state.players`) 새 전송이 필요 없다.
   * 여기서는 **거를 뿐**이고, 실제 판정(마을·거리·중복 거래)은 서버가 다시 한다 —
   * 화면이 거른 것을 서버가 믿으면 그게 곧 클라 권한이 된다.
   */
  const me = ctx.y;
  const near = [], far = [];
  const add = (o) => {
    const d = Math.hypot(o.x - me.x, o.y - me.y);
    (d <= TRADE_RANGE ? near : far).push({ ...o, d });
  };
  for (const p of S.players) if (!p.d && !isResident(p) && p.id !== me.id) add(p);
  near.sort((a, b) => a.d - b.d);

  let list;
  if (!inTown()) {
    list = muted('거래는 <b>마을에서만</b> 한다. 마을로 돌아가면 가까운 사람이 여기 뜬다.');
  } else if (near.length) {
    list = '<div class="row wrap">' + near.map((p) =>
      `<button onclick="act.tradeAsk('${esc(p.id)}')">${starMark(p.n)}${esc(p.n)} <em>Lv.${levelLabel(p.lv)}</em></button>`,
    ).join('') + '</div>'
      + (far.length ? muted(`${far.length}명이 더 보이지만 ${TRADE_RANGE}타일 밖이다.`) : '');
  } else {
    list = muted(far.length
      ? `가까이에 아무도 없다. ${far.length}명이 보이지만 ${TRADE_RANGE}타일 밖이다.`
      : '가까이에 아무도 없다.');
  }

  // R227 ① — 거래는 강공(Ctrl+클릭) 몸짓에서 떼어 냈다: 마을에서 사람을 누르면 이름표 카드가 뜬다.
  // R231 — 거래만 창고 은화를 쓰지 않는 이유를 화면에 적는다(상점·강화·탑 입장은 가방이 모자라면 창고에서 꺼낸다).
  // Q276 — 긴 안내는 한 줄 요약 + [자세히]
  return '<section class="ux-hero"><p class="ux-kicker">물건·은화 교환</p><h3 class="ux-title">누구와 거래할까</h3><p class="ux-summary">아래에서 상대를 고르면 거래를 요청한다.</p></section>'
    + sec('가까운 사람', list + fold(`마을에서 ${TRADE_RANGE}타일 안 · 가방 은화만 올린다`,
    `<b>마을에서</b> 사람을 누르면 이름표 카드가 뜬다 — <b>[거래 신청]</b>을 누르거나 위 목록에서 바로 신청한다.<br>`
    + `서로 ${TRADE_RANGE}타일 안에 있어야 하고, 자리를 뜨면 취소된다.<br>`
    + '갱도·사냥터에서는 거래할 수 없다.<br>'
    + '거래에 올리는 은화는 <b>가방 은화</b>뿐이다 — 상점·강화처럼 창고에서 모자란 몫을 꺼내지 않는다. '
    + '두 사람이 서로 본 조건 그대로 맞바꾸는 자리라, 올린 액수가 확정 순간 가방에서 그대로 오가야 하기 때문이다(필요하면 창고에서 먼저 꺼내 둔다).'))
    + tradeLog();
}

/**
 * 최근 거래 기록 — **접어 둔다.**
 *
 * 거래하러 온 사람이 먼저 볼 것은 지금 누구와 할 수 있는가이지 지난 기록이 아니다.
 * 그런데 기록은 *"내가 뭘 줬더라"* 를 확인하는 자리라 **없으면 안 된다** —
 * 재화가 오간 뒤에 확인할 데가 없으면 그건 분쟁의 씨앗이다.
 * 그래서 목록 아래에 접어 두고, 필요할 때만 편다.
 *
 * 시각은 **사람이 읽는 말**로 적는다. 원시 타임스탬프는 정보가 아니다.
 */
function tradeLog() {
  const rows = S.tradeLog || [];
  if (!rows.length) return '';
  const body = rows.slice().reverse().map((r) => `<div class="item">
      <span class="nm">${esc(r.with || '?')} <span class="qt">${ago(r.at)}</span>
        <br><span class="qt">주었다 ${esc(r.gave || '-')}</span>
        <br><span class="qt">받았다 ${esc(r.got || '-')}</span></span>
    </div>`).join('');
  return lore(`최근 거래 <em>${rows.length}건</em>`, body);
}

/** "몇 분 전" — 하루가 넘으면 날짜로 */
function ago(atSec) {
  const s = Math.max(0, Date.now() / 1000 - (atSec || 0));
  if (s < 60) return '방금';
  if (s < 3600) return `${Math.floor(s / 60)}분 전`;
  if (s < 86400) return `${Math.floor(s / 3600)}시간 전`;
  const d = new Date((atSec || 0) * 1000);
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
}

/** 거래가 열려 있을 때의 화면 */
function renderOpen(ctx, t, mine, theirs) {
  const eqOf = (id) => (ctx.y.inv.equips || []).find((e) => e.id === id);
  /**
   * 상대 장비는 **내 가방에 없다.** 서버가 라벨(`theirGear`)을 같이 보내주므로
   * 그것으로 같은 줄을 그린다 — 이건 사본이 아니라 그리기용 표시다.
   * 교환 자체는 확정 시점에 서버가 실물을 다시 본다(14.43).
   */
  const theirEq = (id) => { const g = (t.theirGear || {})[id]; return g ? { id, ...g } : null; };

  // 상대 칸이 위다 — 잘못 보는 것은 늘 상대가 주는 쪽이다
  let h = '<section class="ux-hero"><p class="ux-kicker">진행 중인 교환</p><h3 class="ux-title">' + esc(t.with.name) + ' 님과 거래 중</h3>'
    + '<div class="ux-facts"><div class="ux-fact"><span>나</span><b>' + (mine.ok ? '확정함' : '조건 확인 중') + '</b></div>'
    + '<div class="ux-fact"><span>상대</span><b>' + (theirs.ok ? '확정함' : '조건 확인 중') + '</b></div></div></section>';
  h += sec(`${esc(t.with.name)} <em>Lv.${levelLabel(t.with.level)}</em> 님에게 받는 것`,
    lines(theirs, theirEq)
    + (theirs.ok
      ? '<div class="kv"><span style="color:#7fc47f">☑ 확정했다</span><b></b></div>'
      : muted('아직 확정하지 않았다.')));

  h += sec('내가 주는 것', lines(mine, eqOf));
  h += '<details class="ux-details" id="trade-offer-picker" open><summary>내 가방에서 올릴 것 고르기</summary>' + myBag(ctx, mine) + '</details>';

  /**
   * **확정이 왜 풀렸는지 말한다.**
   * 목록이 바뀌면 서버가 양쪽 `ok` 를 내리는데, 조용히 내리면 사람은
   * 여전히 확정한 줄 안다. 그 순간이 정확히 사기가 성립하는 자리다.
   */
  /**
   * **막힌 이유는 거래창 안에 띄운다.** 채팅으로만 말하면 모바일에서 접혀 있어 안 읽히고,
   * 그러면 「확정했는데 상대 확정만 풀렸다」로 보인다(운영 신고 2026-09-16).
   */
  const action = `<div class="fname"><b>거래 확정</b></div>`
    + (t.fail ? `<div class="kv" style="color:#e07070"><span>⚠ ${esc(t.fail)}</span><b></b></div>` : '')
    + muted('두 사람 모두 확정하면 위 조건으로 교환된다. 품목·수량·은화가 바뀌면 <b>양쪽의 확정이 취소된다.</b>')
    + `<div class="row">
        <button class="wide ${mine.ok ? 'sel' : 'ux-primary'}" onclick="act.tradeOk()">${mine.ok ? '☑ 내 확정 취소하기' : '위 조건으로 확정하기'}</button>
        <button onclick="act.tradeEnd()">거래 취소</button>
      </div>`;
  /**
   * `EZ` — 올린 물건이 많아져도 확정과 그만두기는 목록 끝으로 밀리지 않는다.
   * 하단 띠는 `position` 덮개가 아니라 스크롤 목록 다음의 형제라 물건을 가리지 않는다.
   */
  return split('', h, action);
}
